import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    tagline: 'Your trusted source for collectible stamps from North Cyprus and beyond.',
    quickLinks: 'Quick Links',
    home: 'Home',
    shop: 'Shop',
    about: 'About',
    contact: 'Contact',
    categories: 'Categories',
    allStamps: 'All Stamps',
    connect: 'Connect',
    ebayStore: 'eBay Store',
    delcampeStore: 'Delcampe Store',
    blog: 'Blog',
    legal: 'Legal',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    returnPolicy: 'Return & Refund',
    cookiePolicy: 'Cookie Policy',
    faq: 'FAQ',
    copyright: 'North Cyprus Philately. All rights reserved. Powered by Ahmet\'s passion for stamps.'
  },
  tr: {
    tagline: 'Kuzey Kıbrıs ve dünyanın her yerinden koleksiyonluk pullar için güvenilir kaynağınız.',
    quickLinks: 'Hızlı Bağlantılar',
    home: 'Ana Sayfa',
    shop: 'Mağaza',
    about: 'Hakkımızda',
    contact: 'İletişim',
    categories: 'Kategoriler',
    allStamps: 'Tüm Pullar',
    connect: 'Bağlantı',
    ebayStore: 'eBay Mağazası',
    delcampeStore: 'Delcampe Mağazası',
    blog: 'Blog',
    legal: 'Yasal',
    terms: 'Hizmet Şartları',
    privacy: 'Gizlilik Politikası',
    returnPolicy: 'İade ve Para İade',
    cookiePolicy: 'Çerez Politikası',
    faq: 'SSS',
    copyright: 'Kuzey Kıbrıs Filatelistlik. Tüm hakları saklıdır. Ahmet\'in pul tutkusuyla güçlendirilmiştir.'
  },
  el: {
    tagline: 'Η αξιόπιστη πηγή σας για συλλεκτικά γραμματόσημα από τη Βόρεια Κύπρο και όχι μόνο.',
    quickLinks: 'Γρήγοροι Σύνδεσμοι',
    home: 'Αρχική',
    shop: 'Κατάστημα',
    about: 'Σχετικά',
    contact: 'Επικοινωνία',
    categories: 'Κατηγορίες',
    allStamps: 'Όλα τα Γραμματόσημα',
    connect: 'Σύνδεση',
    ebayStore: 'Κατάστημα eBay',
    delcampeStore: 'Κατάστημα Delcampe',
    blog: 'Ιστολόγιο',
    copyright: 'Βόρεια Κύπρος Φιλοτελισμός. Με την επιφύλαξη παντός δικαιώματος.'
  },
  de: {
    tagline: 'Ihre vertrauenswürdige Quelle für Sammlerbriefmarken aus Nordzypern und darüber hinaus.',
    quickLinks: 'Schnelllinks',
    home: 'Startseite',
    shop: 'Geschäft',
    about: 'Über uns',
    contact: 'Kontakt',
    categories: 'Kategorien',
    allStamps: 'Alle Briefmarken',
    connect: 'Verbinden',
    ebayStore: 'eBay Shop',
    delcampeStore: 'Delcampe Shop',
    blog: 'Blog',
    copyright: 'Nordzypern Philatelie. Alle Rechte vorbehalten.'
  }
};

const Footer = () => {
  const { language } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [parentCategories, setParentCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Filter only parent categories (level 0 or no parent_id)
      const parents = response.data.filter(cat => cat.level === 0 || !cat.parent_id);
      setParentCategories(parents.slice(0, 6)); // Limit to 6 categories
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  return (
    <footer className="footer">
      {/* Shipping & Discount Info Banner */}
      <div className="bg-gradient-to-r from-green-700 to-blue-700 text-white py-4 border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center text-sm">
            <div className="flex flex-col items-center">
              <span className="text-2xl mb-1">🚢</span>
              <span className="font-semibold">Registered Mail</span>
              <span className="text-xs opacity-90">Only $4 flat rate</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-2xl mb-1">✓</span>
              <span className="font-semibold">Free Shipping</span>
              <span className="text-xs opacity-90">On orders over $100</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-2xl mb-1">🎁</span>
              <span className="font-semibold">10% Discount</span>
              <span className="text-xs opacity-90">On orders over $100</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8 mt-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_philately-shop/artifacts/18b13d6g_3563d1ec-2a1f-46bc-bd18-8bdef0436e03.png" 
                alt="North Cyprus Philately - TRNC Stamp Shop"
                className="h-12 w-12"
                loading="lazy"
                width="48"
                height="48"
              />
              <h3 className="text-xl font-bold">North Cyprus Philately</h3>
            </div>
            <p className="text-sm text-gray-300">{t.tagline}</p>
          </div>
          
          <nav aria-label="Quick Links">
            <h4 className="font-semibold mb-4">{t.quickLinks}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-[#C8A951] transition">{t.home}</Link></li>
              <li><Link to="/shop" className="hover:text-[#C8A951] transition">{t.shop}</Link></li>
              <li><Link to="/about" className="hover:text-[#C8A951] transition">{t.about}</Link></li>
              <li><Link to="/blog" className="hover:text-[#C8A951] transition">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-[#C8A951] transition">{t.contact}</Link></li>
              <li><Link to="/faq" className="hover:text-[#C8A951] transition">FAQ</Link></li>
            </ul>
          </nav>

          <nav aria-label="Legal">
            <h4 className="font-semibold mb-4">{t.legal}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/terms" className="hover:text-[#C8A951] transition">{t.terms}</Link></li>
              <li><Link to="/privacy" className="hover:text-[#C8A951] transition">{t.privacy}</Link></li>
              <li><Link to="/return-policy" className="hover:text-[#C8A951] transition">{t.returnPolicy}</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-[#C8A951] transition">{t.cookiePolicy}</Link></li>
              <li><Link to="/faq" className="hover:text-[#C8A951] transition">{t.faq}</Link></li>
            </ul>
          </nav>

          <nav aria-label="Categories">
            <h4 className="font-semibold mb-4">{t.categories}</h4>
            <ul className="space-y-2 text-sm">
              {parentCategories.length > 0 ? (
                parentCategories.map(cat => (
                  <li key={cat.id}>
                    <Link to={`/shop?category=${cat.slug || cat.name}`} className="hover:text-[#C8A951] transition">
                      {cat[`name_${language}`] || cat.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li><Link to="/shop" className="hover:text-[#C8A951] transition">{t.allStamps}</Link></li>
              )}
            </ul>
          </nav>

          <div>
            <h4 className="font-semibold mb-4">{t.connect}</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="https://www.ebay.com/usr/north_cyprus_philately" target="_blank" rel="noopener noreferrer" className="hover:text-[#C8A951] transition">{t.ebayStore}</a></li>
              <li><a href="https://www.delcampe.net/en_GB/collectables/shop/NorthCyprusPhilately" target="_blank" rel="noopener noreferrer" className="hover:text-[#C8A951] transition">{t.delcampeStore}</a></li>
              <li><Link to="/blog" className="hover:text-[#C8A951] transition">{t.blog}</Link></li>
            </ul>
            <address className="not-italic text-sm text-gray-400 mt-4">
              <p>northcyprusphilately@gmail.com</p>
              <p>+357 9629 6207</p>
              <p>Lefkoşa, North Cyprus</p>
            </address>
          </div>
        </div>
        
        <div className="border-t border-gray-700 pt-6 text-center text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} {t.copyright}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;